import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { getStoredToken } from './lib/api';
import { homeForRole } from './lib/authRoutes';
import { isPlatformPanelUser } from './lib/platformRoles';
import { ToastProvider } from './lib/Toast';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import SuperAdmin from './pages/SuperAdmin';
import RestaurantLayout from './pages/RestaurantLayout';
import Overview from './pages/Overview';
import MenuPage from './pages/MenuPage';
import TablesPage from './pages/TablesPage';
import StaffPage from './pages/StaffPage';
import OrdersPage from './pages/OrdersPage';
import KitchenPage from './pages/KitchenPage';
import WaiterPage from './pages/WaiterPage';
import StatsPage from './pages/StatsPage';
import SettingsPage from './pages/SettingsPage';
import LocationsPage from './pages/LocationsPage';
import ClientMenu from './pages/ClientMenu';
import PinLogin from './pages/PinLogin';
import Contact from './pages/Contact';
import SubscriptionResult from './pages/SubscriptionResult';
import LegalPrivacy from './pages/LegalPrivacy';
import LegalTerms from './pages/LegalTerms';
import LegalCookies from './pages/LegalCookies';
import { CookieNoticeBanner } from './components/CookieNoticeBanner';
import { ScrollToTop } from './lib/ScrollToTop';

function isRoleAllowed(user, roles) {
    if (!roles?.length) return true;
    if (isPlatformPanelUser(user.role)) {
        if (roles.some((r) => isPlatformPanelUser(r))) return true;
        return Boolean(user.restaurant_id);
    }
    return roles.includes(user.role);
}

function Protected({ roles, children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    const hasToken = Boolean(getStoredToken());
    if (loading || (hasToken && !user)) {
        return <div style={{ padding: '4rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
    }
    if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
    if (!isRoleAllowed(user, roles)) {
        if (isPlatformPanelUser(user.role)) return <Navigate to="/admin" replace />;
        if (['restaurant_admin', 'manager', 'waiter', 'kitchen'].includes(user.role)) {
            return <Navigate to={homeForRole(user.role)} replace />;
        }
        return <Navigate to="/" replace />;
    }
    return children;
}

function App() {
    return (
        <AuthProvider>
            <ToastProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/pin" element={<PinLogin />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/legal/privacy" element={<LegalPrivacy />} />
                        <Route path="/legal/terms" element={<LegalTerms />} />
                        <Route path="/legal/cookies" element={<LegalCookies />} />
                        <Route path="/subscription/success" element={<Protected roles={['restaurant_admin', 'manager']}><SubscriptionResult outcome="success" /></Protected>} />
                        <Route path="/subscription/cancel" element={<Protected roles={['restaurant_admin', 'manager']}><SubscriptionResult outcome="cancel" /></Protected>} />
                        <Route path="/m/:slug/:table_token" element={<ClientMenu />} />
                        <Route path="/admin" element={
                            <Protected roles={['super_admin', 'platform_admin']}><SuperAdmin /></Protected>
                        } />
                        <Route path="/r" element={
                            <Protected roles={['restaurant_admin', 'manager', 'waiter', 'kitchen']}>
                                <RestaurantLayout />
                            </Protected>
                        }>
                            <Route index element={<Overview />} />
                            <Route path="menu" element={<MenuPage />} />
                            <Route path="tables" element={<TablesPage />} />
                            <Route path="staff" element={<StaffPage />} />
                            <Route path="orders" element={<OrdersPage />} />
                            <Route path="kitchen" element={<KitchenPage />} />
                            <Route path="waiter" element={<WaiterPage />} />
                            <Route path="stats" element={<StatsPage />} />
                            <Route path="locations" element={<LocationsPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <CookieNoticeBanner />
                </BrowserRouter>
            </ToastProvider>
        </AuthProvider>
    );
}

export default App;
